import { Box, Paper, Stack, Typography, CircularProgress, Button, ButtonGroup, TextField, Checkbox, FormControlLabel } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { selectAdmins } from '../../../redux/slices/adminsSlice';
import axios from '../../../resources/axios';

interface DailyData {
  date: string;
  [key: string]: number | string;
}

interface SubscriptionPlan {
  _id: string;
  name: string;
  color?: string;
}

type DateRange = 'today' | 'week' | 'month' | 'custom';

const CHART_COLORS = [
  '#1976D2', // Blue
  '#388E3C', // Green
  '#D32F2F', // Red
  '#F57C00', // Orange
  '#7B1FA2', // Purple
  '#C2185B', // Pink
  '#0097A7', // Cyan
  '#5D4037', // Brown
  '#455A64', // Blue Grey
  '#E64A19', // Deep Orange
];

const SubscriptionStatsChart = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const adminInfo = useSelector(selectAdmins);

  const [dailyData, setDailyData] = useState<DailyData[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>('month');
  const [customStartDate, setCustomStartDate] = useState<string>(moment().subtract(1, 'month').format('YYYY-MM-DD'));
  const [customEndDate, setCustomEndDate] = useState<string>(moment().format('YYYY-MM-DD'));
  const [showSeparate, setShowSeparate] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [hiddenPlans, setHiddenPlans] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchStatsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, customStartDate, customEndDate, adminInfo.currentCity, showSeparate]);

  const getDateRange = () => {
    const now = moment();
    switch (dateRange) {
      case 'today':
        return { start: now.clone().startOf('day'), end: now.clone().endOf('day'), days: 1 };
      case 'week':
        return { start: now.clone().subtract(7, 'days').startOf('day'), end: now.clone().endOf('day'), days: 7 };
      case 'month':
        return { start: now.clone().subtract(30, 'days').startOf('day'), end: now.clone().endOf('day'), days: 30 };
      case 'custom':
        if (customStartDate && customEndDate) {
          const start = moment(customStartDate);
          const end = moment(customEndDate);
          const days = end.diff(start, 'days') + 1;
          return { start: start.startOf('day'), end: end.endOf('day'), days };
        }
        return { start: now.clone().subtract(30, 'days').startOf('day'), end: now.clone().endOf('day'), days: 30 };
      default:
        return { start: now.clone().subtract(30, 'days').startOf('day'), end: now.clone().endOf('day'), days: 30 };
    }
  };

  const fetchStatsData = async () => {
    setLoading(true);
    const data: DailyData[] = [];
    const { start, days } = getDateRange();
    const plansMap = new Map<string, SubscriptionPlan>();

    try {
      const userInfo = JSON.parse(localStorage.getItem('STORE_USER_INFO') || '{}');
      const token = userInfo.token;

      const response = await axios.get('/subscription/admin/user_subscriptions', {
        headers: {
          'x-access-token': token
        },
        params: {
          limit: 10000,
          page: 1
        }
      });

      const allSubscriptions = response.data.data.subscriptions || [];
      const cityFiltered = allSubscriptions.filter(
        (sub: any) => sub.city._id === adminInfo.currentCity
      );

      if (showSeparate) {
        cityFiltered.forEach((sub: any) => {
          if (sub.subscriptionPlan && !plansMap.has(sub.subscriptionPlan._id)) {
            plansMap.set(sub.subscriptionPlan._id, {
              _id: sub.subscriptionPlan._id,
              name: sub.subscriptionPlan.name || t('subscriptions.noName')
            });
          }
        });

        const plans = Array.from(plansMap.values());
        setSubscriptionPlans(plans);

        for (let i = days - 1; i >= 0; i--) {
          const dateStart = start.clone().add(days - 1 - i, 'days').startOf('day');
          const dateEnd = dateStart.clone().endOf('day');
          const dateStr = dateStart.format('YYYY-MM-DD');

          const dayData: DailyData = { date: dateStr };

          plans.forEach((plan) => {
            const count = cityFiltered.filter((sub: any) => {
              const createdAt = moment(sub.createdAt);
              const isPlanMatch = sub.subscriptionPlan?._id === plan._id;
              const isDateMatch = createdAt.isBetween(dateStart, dateEnd, undefined, '[]');
              return isPlanMatch && isDateMatch;
            }).length;

            dayData[plan.name] = count;
          });

          data.push(dayData);
        }
      } else {
        for (let i = days - 1; i >= 0; i--) {
          const dateStart = start.clone().add(days - 1 - i, 'days').startOf('day');
          const dateEnd = dateStart.clone().endOf('day');
          const dateStr = dateStart.format('YYYY-MM-DD');

          const subscriptionsCount = cityFiltered.filter((sub: any) => {
            const createdAt = moment(sub.createdAt);
            return createdAt.isBetween(dateStart, dateEnd, undefined, '[]');
          }).length;

          data.push({
            date: dateStr,
            subscriptions: subscriptionsCount
          });
        }
      }

      setDailyData(data);
    } catch (error) {
      console.error('Error fetching subscription stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#1E1E1E',
            border: `1px solid ${theme.palette.mode === 'light' ? '#E0E0E0' : '#424242'}`,
            borderRadius: '8px',
            padding: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {payload[0]?.payload?.date}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ color: entry.color, fontSize: '0.875rem' }}
            >
              {entry.name}: {entry.value}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  const getLineColor = (index: number, isDarkMode: boolean) => {
    if (isDarkMode) {
      return CHART_COLORS[index % CHART_COLORS.length];
    }
    return CHART_COLORS[index % CHART_COLORS.length];
  };

  const togglePlanVisibility = (planId: string) => {
    setHiddenPlans((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(planId)) {
        newSet.delete(planId);
      } else {
        newSet.add(planId);
      }
      return newSet;
    });
  };

  const visiblePlans = subscriptionPlans.filter(plan => !hiddenPlans.has(plan._id));

  const CustomLegend = (props: any) => {
    const { payload } = props;
    
    if (!showSeparate) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2, pt: 2 }}>
          {payload?.map((entry: any, index: number) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 16,
                  height: 3,
                  backgroundColor: entry.color,
                  borderRadius: 1
                }}
              />
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0' }}>
                {entry.value}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2, pt: 2 }}>
        {subscriptionPlans.map((plan, index) => {
          const originalIndex = subscriptionPlans.findIndex(p => p._id === plan._id);
          const color = getLineColor(originalIndex, theme.palette.mode === 'dark');
          
          return (
            <FormControlLabel
              key={plan._id}
              control={
                <Checkbox
                  checked={!hiddenPlans.has(plan._id)}
                  onChange={() => togglePlanVisibility(plan._id)}
                  size="small"
                  sx={{
                    color: color,
                    '&.Mui-checked': {
                      color: color,
                    },
                    padding: '4px'
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 3,
                      backgroundColor: color,
                      borderRadius: 1
                    }}
                  />
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', color: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0' }}>
                    {plan.name}
                  </Typography>
                </Box>
              }
              sx={{ margin: 0 }}
            />
          );
        })}
      </Box>
    );
  };

  return (
    <Paper
      sx={{
        padding: '24px',
        border: theme.palette.mode === 'light' ? '1px solid rgba(0, 0, 0, 0.08)' : 'none',
        borderRadius: 3,
        minHeight: 450,
        mb: 3
      }}
    >
      <Stack spacing={3}>
        <Box>
          <Typography
            variant='h6'
            sx={{
              fontWeight: 600,
              fontSize: 20,
              color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF',
              mb: 0.5
            }}
          >
            {t('subscriptions.statsTitle')}
          </Typography>
          <Typography
            variant='body2'
            sx={{
              color: theme.palette.mode === 'light' ? '#757575' : '#9E9E9E',
              fontSize: '0.875rem'
            }}
          >
            {showSeparate 
              ? t('subscriptions.statsByPlan') 
              : t('subscriptions.statsByDay')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <ButtonGroup variant="outlined" size="small">
            <Button
              onClick={() => setDateRange('today')}
              variant={dateRange === 'today' ? 'contained' : 'outlined'}
              sx={{
                backgroundColor: dateRange === 'today' 
                  ? '#5073F8'
                  : 'transparent',
                color: dateRange === 'today' 
                  ? '#FFF'
                  : (theme.palette.mode === 'light' ? '#616161' : '#B0B0B0'),
                borderColor: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242',
                '&:hover': {
                  backgroundColor: dateRange === 'today' 
                    ? '#3D5BD4'
                    : (theme.palette.mode === 'light' ? '#F5F5F5' : '#2C2C2C'),
                  borderColor: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242',
                }
              }}
            >
              {t('subscriptions.today')}
            </Button>
            <Button
              onClick={() => setDateRange('week')}
              variant={dateRange === 'week' ? 'contained' : 'outlined'}
              sx={{
                backgroundColor: dateRange === 'week' 
                  ? '#5073F8'
                  : 'transparent',
                color: dateRange === 'week' 
                  ? '#FFF'
                  : (theme.palette.mode === 'light' ? '#616161' : '#B0B0B0'),
                borderColor: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242',
                '&:hover': {
                  backgroundColor: dateRange === 'week' 
                    ? '#3D5BD4'
                    : (theme.palette.mode === 'light' ? '#F5F5F5' : '#2C2C2C'),
                  borderColor: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242',
                }
              }}
            >
              {t('subscriptions.sevenDays')}
            </Button>
            <Button
              onClick={() => setDateRange('month')}
              variant={dateRange === 'month' ? 'contained' : 'outlined'}
              sx={{
                backgroundColor: dateRange === 'month' 
                  ? '#5073F8'
                  : 'transparent',
                color: dateRange === 'month' 
                  ? '#FFF'
                  : (theme.palette.mode === 'light' ? '#616161' : '#B0B0B0'),
                borderColor: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242',
                '&:hover': {
                  backgroundColor: dateRange === 'month' 
                    ? '#3D5BD4'
                    : (theme.palette.mode === 'light' ? '#F5F5F5' : '#2C2C2C'),
                  borderColor: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242',
                }
              }}
            >
              {t('subscriptions.month')}
            </Button>
            <Button
              onClick={() => setDateRange('custom')}
              variant={dateRange === 'custom' ? 'contained' : 'outlined'}
              sx={{
                backgroundColor: dateRange === 'custom' 
                  ? '#5073F8'
                  : 'transparent',
                color: dateRange === 'custom' 
                  ? '#FFF'
                  : (theme.palette.mode === 'light' ? '#616161' : '#B0B0B0'),
                borderColor: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242',
                '&:hover': {
                  backgroundColor: dateRange === 'custom' 
                    ? '#3D5BD4'
                    : (theme.palette.mode === 'light' ? '#F5F5F5' : '#2C2C2C'),
                  borderColor: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242',
                }
              }}
            >
              {t('subscriptions.custom')}
            </Button>
          </ButtonGroup>

          <Button
            onClick={() => setShowSeparate(!showSeparate)}
            variant={showSeparate ? 'contained' : 'outlined'}
            size="small"
            sx={{
              backgroundColor: showSeparate 
                ? '#5073F8'
                : 'transparent',
              color: showSeparate 
                ? '#FFF'
                : (theme.palette.mode === 'light' ? '#616161' : '#B0B0B0'),
              borderColor: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242',
              '&:hover': {
                backgroundColor: showSeparate 
                  ? '#3D5BD4'
                  : (theme.palette.mode === 'light' ? '#F5F5F5' : '#2C2C2C'),
                borderColor: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242',
              }
            }}
          >
            {showSeparate ? t('subscriptions.showAll') : t('subscriptions.showSeparate')}
          </Button>

          {dateRange === 'custom' && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label={t('subscriptions.from')}
                type="date"
                size="small"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  minWidth: 150,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2C2C2C',
                  }
                }}
              />
              <TextField
                label={t('subscriptions.to')}
                type="date"
                size="small"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  minWidth: 150,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2C2C2C',
                  }
                }}
              />
            </Box>
          )}
        </Box>

        <Box sx={{ width: '100%', height: 380, position: 'relative' }}>
          {loading && (
            <Box sx={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}>
              <CircularProgress />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: theme.palette.mode === 'light' ? '#757575' : '#9E9E9E',
                  textAlign: 'center',
                  maxWidth: 250
                }}
              >
                {t('subscriptions.loadingStats')}
              </Typography>
            </Box>
          )}
          <ResponsiveContainer width='100%' height='100%'>
            <LineChart 
              data={dailyData} 
              margin={{ top: 20, right: 50, left: 20, bottom: 20 }}
            >
              <CartesianGrid 
                strokeDasharray='3 3' 
                stroke={theme.palette.mode === 'light' ? '#E0E0E0' : '#424242'}
                vertical={false}
              />
              <XAxis 
                dataKey='date'
                tick={{ 
                  fill: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
                  fontSize: 11
                }}
                tickMargin={10}
                axisLine={{ stroke: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242' }}
              />
              <YAxis 
                tick={{ 
                  fill: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
                  fontSize: 11
                }}
                axisLine={{ stroke: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                content={CustomLegend}
              />
              {!showSeparate ? (
                <Line
                  type="monotone"
                  dataKey="subscriptions"
                  name={t('subscriptions.subscriptionsLine')}
                  stroke={theme.palette.mode === 'light' ? '#1976D2' : '#5073F8'}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              ) : (
                visiblePlans.map((plan, index) => {
                  const originalIndex = subscriptionPlans.findIndex(p => p._id === plan._id);
                  return (
                    <Line
                      key={plan._id}
                      type="monotone"
                      dataKey={plan.name}
                      name={plan.name}
                      stroke={getLineColor(originalIndex, theme.palette.mode === 'dark')}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  );
                })
              )}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </Paper>
  );
};

export default SubscriptionStatsChart;
